// Security shell — daily-driver layout for the security surface.
// Mirrors AdminShell's grouped-sidebar pattern (v190) but with the
// cyan daily-driver accent rather than the nebula-violet governance
// accent: a SOC analyst spends real time here, so the shell should
// feel like the rest of the dashboard, not the occasional-use admin
// area.
//
// Three groups (Enforcement / Intelligence / Audit) prepared for
// future Pull-Gate-Decisions and Auto-Quarantine-Settings landing
// without further layout refactor.
//
// The 5 child pages (Blocks, New block, Advisories, Who pulled,
// Audit log) used to render their own AppShell + per-page Back-link.
// They now render <SecurityShell title="…" description="…">{body}</SecurityShell>.
// The Back-to-blocks link is no longer needed — the sidebar carries
// every navigation target.

import { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import {
  ArrowLeft, ScrollText, ShieldAlert, AlertOctagon, Plus, Users,
  HelpCircle, Lock, Menu, ShieldCheck, X,
  type LucideIcon,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { Wordmark } from './Logo';
import { PageContainer } from './PageContainer';
import { ThemeToggle } from './ui/ThemeToggle';
import { SearchBar } from './SearchBar';
import { UserMenu } from './UserMenu';
import { cn } from '../lib/cn';
import { useMaintenance } from '../api/maintenance';
import { usePublicConfig } from '../api/publicConfig';
import { MaintenanceBanner } from './MaintenanceBanner';
import { SystemBanner } from './SystemBanner';
import { SeedBanner } from './SeedBanner';

interface SecurityTab {
  to: string;
  label: string;
  Icon: LucideIcon;
}

interface SecurityGroup {
  label: string;       // section heading (small caps)
  items: SecurityTab[];
}

const groups: SecurityGroup[] = [
  {
    label: 'Enforcement',
    items: [
      { to: '/security', label: 'Blocks', Icon: ShieldAlert },
      { to: '/security/new', label: 'New block', Icon: Plus },
    ],
  },
  {
    label: 'Intelligence',
    items: [
      { to: '/security/advisories', label: 'Advisories', Icon: AlertOctagon },
    ],
  },
  {
    label: 'Audit',
    items: [
      { to: '/security/who-pulled', label: 'Who pulled', Icon: Users },
      { to: '/security/audit', label: 'Audit log', Icon: ScrollText },
    ],
  },
];

const flatTabs: SecurityTab[] = groups.flatMap((g) => g.items);

function SidebarNav({ onNavigate }: Readonly<{ onNavigate?: () => void }>) {
  return (
    <nav className="space-y-6 py-2">
      {groups.map((group) => (
        <div key={group.label} className="space-y-1">
          <p className="orb-section-title px-3 mb-1.5 text-fg-subtle">
            {group.label}
          </p>
          {group.items.map(({ to, label, Icon }) => {
            const hasChildTab = flatTabs.some(
              (o) => o.to !== to && o.to.startsWith(to + '/'),
            );
            return (
              <NavLink
                key={to}
                to={to}
                end={to === '/security' || hasChildTab}
                onClick={onNavigate}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2.5 px-3 py-1.5 rounded-md text-sm transition-colors',
                    isActive
                      ? 'bg-accent/10 text-accent border border-accent/30'
                      : 'text-fg-muted border border-transparent hover:text-fg hover:bg-bg-hover/40',
                  )
                }
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="truncate">{label}</span>
              </NavLink>
            );
          })}
        </div>
      ))}
    </nav>
  );
}

export function SecurityShell({ children, title, description, eyebrow }: Readonly<{
  children: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  // Optional small-caps line above the heading (e.g. "Enforcement",
  // "Intelligence"). Defaults to "Security" when omitted.
  eyebrow?: string;
}>) {
  const { data: maint } = useMaintenance();
  const { data: pubConfig } = usePublicConfig();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  return (
    <>
      <div className="starfield-bg" aria-hidden="true" />
      <div className="min-h-screen flex flex-col">
        <header className="relative z-40 h-16 border-b border-border bg-bg-elevated/60 backdrop-blur-sm flex items-center justify-between gap-6 px-6 shrink-0">
          <div className="flex items-center gap-4 shrink-0">
            <button
              type="button"
              onClick={() => setMobileNavOpen((v) => !v)}
              className="lg:hidden inline-flex items-center justify-center w-9 h-9 rounded-md text-fg-muted hover:text-fg hover:bg-bg-hover transition-colors"
              aria-label="Toggle security navigation"
            >
              {mobileNavOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            <Link to="/" className="flex items-center gap-2 text-fg-muted hover:text-fg text-sm">
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Back to app</span>
            </Link>
            <span className="text-fg-subtle hidden sm:inline">/</span>
            <span className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-accent" />
              <span className="orb-nav-label text-accent">Security</span>
            </span>
          </div>
          <div className="flex-1 max-w-3xl min-w-0 hidden md:block">
            <SearchBar />
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {pubConfig?.air_gapped_mode && (
              <Link
                to="/admin/network"
                className="inline-flex items-center gap-1.5 px-2 h-7 rounded-md text-xs font-medium bg-danger/10 text-danger border border-danger/30 hover:bg-danger/20 transition-colors"
                title="This deployment is in air-gapped-by-default mode. Outbound network calls are denied unless an admin opted in."
              >
                <Lock className="h-3 w-3" />
                <span>Air-gapped</span>
              </Link>
            )}
            <Wordmark />
            {pubConfig?.help_url && (
              <a
                href={pubConfig.help_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-9 h-9 rounded-md text-fg-muted hover:text-fg hover:bg-bg-hover transition-colors"
                title="Help & documentation"
                aria-label="Help"
              >
                <HelpCircle className="h-4 w-4" />
              </a>
            )}
            <ThemeToggle />
            <UserMenu />
          </div>
        </header>

        {maint?.active && <MaintenanceBanner reason={maint.reason} until={maint.until} />}
        {pubConfig?.active_banner && <SystemBanner banner={pubConfig.active_banner} />}
        {pubConfig?.seed_active && <SeedBanner setName={pubConfig.seed_set} />}

        <div className="flex-1 flex min-h-0">
          <aside className="hidden lg:block w-[240px] shrink-0 border-r border-border bg-bg-elevated/30 sticky top-16 self-start max-h-[calc(100vh-4rem)] overflow-y-auto">
            <div className="px-3 py-4">
              <SidebarNav />
            </div>
          </aside>

          {mobileNavOpen && (
            <div
              className="lg:hidden fixed inset-0 z-30 bg-black/50 backdrop-blur-sm"
              role="button"
              tabIndex={0}
              aria-label="Close navigation menu"
              onClick={() => setMobileNavOpen(false)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ' || e.key === 'Escape') {
                  e.preventDefault();
                  setMobileNavOpen(false);
                }
              }}
            >
              <div
                className="absolute top-16 left-0 bottom-0 w-72 bg-bg-elevated border-r border-border overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <div className="px-3 py-4">
                  <SidebarNav onNavigate={() => setMobileNavOpen(false)} />
                </div>
              </div>
            </div>
          )}

          <main className="flex-1 min-w-0">
            <PageContainer className="py-10">
              {title && (
                <div className="mb-8">
                  <p className="orb-eyebrow text-accent/80 mb-2">{eyebrow ?? 'Security'}</p>
                  <h1 className="text-3xl font-display font-semibold text-fg leading-tight">{title}</h1>
                  {description && (
                    <p className="text-fg-muted mt-3 max-w-2xl leading-relaxed">{description}</p>
                  )}
                </div>
              )}
              <div className="space-y-6">{children}</div>
            </PageContainer>
          </main>
        </div>
      </div>
    </>
  );
}
